import { invokeFunction, supabase } from '@/integrations/supabase/client';
import type { ManageMerchantContactsResponse, MerchantContactInput } from '@/shared/types/merchantContacts';
import { readFunctionErrorBody } from '@/utils/functionErrorBody';

// Settings › Merchant Contacts goes through the admin-only edge function
// (FIG-345 · UC-228), which also records history and emails the magic link
// when the sign-in email changes.

export type MerchantContactsRequest =
  | { action: 'list'; organisationId: string }
  | { action: 'add'; organisationId: string; contact: MerchantContactInput }
  | { action: 'update'; organisationId: string; contactId: string; contact: MerchantContactInput }
  | { action: 'remove'; organisationId: string; contactId: string }
  | { action: 'promote'; organisationId: string; contactId: string }
  | { action: 'resend_link'; organisationId: string };

export const MERCHANT_CONTACTS_FUNCTION = 'manage-merchant-contacts-v1';

export const merchantContactsQueryKey = (organisationId: string) => ['merchant-contacts', organisationId] as const;

/** Never throws: a failed call comes back as { success: false, error }. */
export async function callMerchantContacts(request: MerchantContactsRequest): Promise<ManageMerchantContactsResponse> {
  try {
    const { data } = await invokeFunction<ManageMerchantContactsResponse>(supabase, MERCHANT_CONTACTS_FUNCTION, { body: request });
    if (data && typeof data === 'object' && 'success' in data) return data;
    return { success: false, error: 'Unexpected response' };
  } catch (error) {
    const body = await readFunctionErrorBody<ManageMerchantContactsResponse>(error);
    if (body && typeof body === 'object' && 'success' in body) return { ...body, success: false };
    return { success: false, error: error instanceof Error ? error.message : 'Could not reach the contacts service' };
  }
}
